import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Popconfirm, Select, Typography } from 'antd';
import { getMemberTagReview, saveMemberTagReview } from '@/services/technical';
import {
  REVIEW_DECISIONS,
  readMemberTagReview,
  validReviewReason,
} from './MemberTagReviewSupport';
import { newRetryRequestId } from './FactSyncRetryButton';
import { processingTime } from './CommunicationProcessingPanel';

export default function MemberTagReviewPanel({ memberId, observationId }) {
  const [state, setState] = useState({});
  const [decision, setDecision] = useState('NEEDS_REVIEW');
  const [reason, setReason] = useState('');
  const [related, setRelated] = useState('');
  const busy = useRef(false);
  const generation = useRef(0);
  const request = useRef(null);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const load = async () => {
    if (busy.current) return;
    busy.current = true;
    const version = ++generation.current;
    request.current = null;
    setState({ loading: true });
    setDecision('NEEDS_REVIEW');
    setReason('');
    setRelated('');
    try {
      const result = await getMemberTagReview({
        requestData: { memberId, observationId },
      });
      const data = readMemberTagReview(result, memberId, observationId);
      if (version === generation.current) setState({ data });
    } catch (error) {
      if (version === generation.current) setState({ error: message(error) });
    } finally {
      if (version === generation.current) busy.current = false;
    }
  };
  const save = async () => {
    if (
      busy.current ||
      state.accepted ||
      state.conflict ||
      !state.data ||
      !validReviewReason(reason) ||
      (decision === 'DUPLICATE' &&
        (!/^[A-Za-z0-9_-]{1,64}$/.test(related) || related === observationId))
    )
      return;
    busy.current = true;
    const version = generation.current;
    setState((value) => ({ ...value, saving: true, error: null }));
    try {
      if (!request.current)
        request.current = {
          memberId,
          observationId,
          requestId: newRetryRequestId(),
          expectedSourceHash: state.data.sourceHash,
          expectedReviewId: state.data.latestReviewId,
          decision,
          reason,
          relatedObservationId: decision === 'DUPLICATE' ? related : null,
        };
      const response = await saveMemberTagReview({
        requestData: request.current,
      });
      const data = readMemberTagReview(
        response,
        memberId,
        observationId,
        request.current.requestId,
      );
      if (version === generation.current) setState({ data, accepted: true });
    } catch (error) {
      if (version === generation.current)
        setState((value) => ({
          ...value,
          saving: false,
          conflict: error?.response?.status === 409,
          uncertain: true,
          error: message(error),
        }));
    } finally {
      if (version === generation.current) busy.current = false;
    }
  };
  const disabled =
    state.saving || state.accepted || state.conflict || state.uncertain;
  const validForm =
    validReviewReason(reason) &&
    (decision !== 'DUPLICATE' ||
      (/^[A-Za-z0-9_-]{1,64}$/.test(related) && related !== observationId));
  const data = state.data;
  return (
    <div style={{ marginTop: 12 }}>
      <Typography.Paragraph strong>人工复核</Typography.Paragraph>
      <Typography.Paragraph type="secondary">
        复核意见与模型事实分开保存，不改变能力分值或计划。人工核对不代表医学诊断；重复标记不自动删除或合并标签。
      </Typography.Paragraph>
      <Button
        size="small"
        disabled={state.loading || state.saving}
        onClick={load}
      >
        {state.loading ? '查询中…' : data ? '刷新复核状态' : '查看复核状态'}
      </Button>
      {data?.outcome === 'unavailable' && (
        <Typography.Paragraph>当前标签无法提供复核状态</Typography.Paragraph>
      )}
      {data?.sourceHash && (
        <>
          <Typography.Paragraph type="secondary">
            标签编号：{observationId}
          </Typography.Paragraph>
          {data.history.length === 0 && (
            <Typography.Paragraph>尚无人工复核记录</Typography.Paragraph>
          )}
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {data.history.map((item, index) => (
              <div
                key={item.reviewId}
                style={{ marginTop: 8, borderBottom: '1px solid #eee' }}
              >
                <Typography.Text strong>
                  {index === 0 ? '最新意见' : '历史意见'}：
                  {REVIEW_DECISIONS[item.decision]}
                </Typography.Text>
                <Typography.Paragraph>{item.reason}</Typography.Paragraph>
                {item.relatedObservationId && (
                  <Typography.Paragraph>
                    关联标签：{item.relatedObservationId}
                  </Typography.Paragraph>
                )}
                <Typography.Paragraph
                  type={item.stale ? 'warning' : 'secondary'}
                >
                  {processingTime(item.reviewedAt)}
                  {item.stale ? ' · 源状态或有效期已变化，需重新核对' : ''}
                </Typography.Paragraph>
              </div>
            ))}
          </div>
          {data.hasMore && (
            <Typography.Paragraph type="secondary">
              仅展示最新 10 条，完整历史保留在审计记录中。
            </Typography.Paragraph>
          )}
          <Select
            aria-label="复核结论"
            value={decision}
            disabled={disabled}
            onChange={setDecision}
            style={{ width: '100%', marginTop: 8 }}
          >
            {Object.entries(REVIEW_DECISIONS).map(([key, label]) => (
              <Select.Option key={key} value={key}>
                {label}
              </Select.Option>
            ))}
          </Select>
          <Input
            aria-label="复核原因"
            placeholder="填写核对依据或纠错原因（必填，最多500字）"
            value={reason}
            maxLength={500}
            disabled={disabled}
            onChange={(event) => setReason(event.target.value)}
            style={{ marginTop: 8 }}
          />
          {decision === 'DUPLICATE' && (
            <Input
              aria-label="关联标签编号"
              placeholder="同会员另一条标签编号"
              maxLength={64}
              value={related}
              disabled={disabled}
              onChange={(event) => setRelated(event.target.value)}
              style={{ marginTop: 8 }}
            />
          )}
          {state.accepted ? (
            <Typography.Paragraph>
              复核意见已保存，原始模型事实保持不变。
            </Typography.Paragraph>
          ) : (
            <Popconfirm
              title="确认保存这条人工意见？会保留操作审计，不覆盖模型原始事实。"
              onConfirm={save}
              disabled={state.saving || state.conflict || !validForm}
              okText="确认保存"
              cancelText="取消"
            >
              <Button
                size="small"
                loading={state.saving}
                disabled={state.saving || state.conflict || !validForm}
                style={{ marginTop: 8 }}
              >
                {state.uncertain ? '沿用原请求重试保存' : '保存复核意见'}
              </Button>
            </Popconfirm>
          )}
        </>
      )}
      {state.error && (
        <Typography.Paragraph type="warning">
          {state.error}
        </Typography.Paragraph>
      )}
    </div>
  );
}
function message(error) {
  return (
    {
      401: '请先登录后台',
      403: '无会员档案菜单权限',
      409: '标签或复核状态已变化，请刷新后重新核对',
    }[error?.response?.status] ||
    '结果未确认；保存重试会沿用原请求，刷新后请重新核对'
  );
}
MemberTagReviewPanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  observationId: PropTypes.string.isRequired,
};
